import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Eye, EyeOff, KeyRound, Loader2, UserRound } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/authContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/supabase.config';
import { displayEmail, myAccountKey, useMyAccount } from '../hooks/useMyAccount';

const SCOPE_LABEL = { NATIONAL: 'Nacional', DISTRICT: 'Distrito', REGION: 'Región' } as const;
const MIN_PASSWORD = 8;

/** Error text for Auth password errors */
function passwordError(message: string): string {
  if (/same.*password|different from the old/i.test(message)) return 'La nueva contraseña debe ser distinta de la actual.';
  if (/weak|short|at least/i.test(message)) return `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres.`;
  if (/reauth|recent/i.test(message)) return 'Vuelve a iniciar sesión e inténtalo de nuevo.';
  return 'No se pudo cambiar la contraseña. Inténtalo de nuevo.';
}

export function MyAccountPage() {
  const { user } = useAuth();
  const { data: account, isLoading, isError } = useMyAccount();
  const [params] = useSearchParams();
  const forced = !!account?.must_change_password || params.get('cambiar') === '1';

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  }
  if (isError || !account || !user) {
    return <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No se pudo cargar tu cuenta.</CardContent></Card>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold"><UserRound className="size-6" /> Mi cuenta</h1>
        <p className="text-sm text-muted-foreground">Tus datos de acceso y tu contraseña.</p>
      </div>

      {forced && account.must_change_password && (
        <Alert>
          <KeyRound className="size-4" />
          <AlertDescription>
            Tu cuenta tiene una contraseña temporal. Crea una contraseña nueva para continuar.
          </AlertDescription>
        </Alert>
      )}

      <ProfileCard />
      <RolesCard />
      <PasswordCard authEmail={user.email ?? ''} username={account.username ?? ''} mustChange={account.must_change_password} />
    </div>
  );
}

function ProfileCard() {
  const { user } = useAuth();
  const { data: account } = useMyAccount();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState(account?.full_name ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => setName(account?.full_name ?? ''), [account?.full_name]);
  if (!account) return null;

  const dirty = name.trim() !== (account.full_name ?? '') && name.trim().length > 0;
  const email = displayEmail(account);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    const { error } = await supabase.rpc('update_my_profile', { p_full_name: name });
    setSaving(false);
    if (error) {
      toast({ title: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Nombre actualizado' });
    void qc.invalidateQueries({ queryKey: myAccountKey(user?.id) });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Datos personales</CardTitle>
        <CardDescription>Si algún dato es incorrecto, pide a un administrador que lo corrija.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="acc-name">Nombre</Label>
            <Input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="acc-username">Usuario</Label>
              <Input id="acc-username" value={account.username ?? '—'} disabled className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acc-email">Correo de contacto</Label>
              <Input id="acc-email" value={email ?? 'Sin correo'} disabled />
            </div>
          </div>
          <Button type="submit" disabled={!dirty || saving}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Guardar nombre
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function RolesCard() {
  const { data: account } = useMyAccount();
  if (!account) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Mis roles</CardTitle>
        <CardDescription>Lo que puedes hacer en el sistema. Solo un administrador puede cambiarlos.</CardDescription>
      </CardHeader>
      <CardContent>
        {account.assignments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tienes roles asignados.</p>
        ) : (
          <ul className="space-y-2">
            {account.assignments.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm">
                <span className="font-medium">{a.role?.name ?? '—'}</span>
                <Badge variant="secondary">{SCOPE_LABEL[a.scope]}</Badge>
                {a.scope === 'DISTRICT' && a.district && <span className="text-muted-foreground">{a.district.name}</span>}
                {a.scope === 'REGION' && a.region && <span className="text-muted-foreground">{a.region.name}</span>}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PasswordCard({ authEmail, username, mustChange }: { authEmail: string; username: string; mustChange: boolean }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem =
    next && next.length < MIN_PASSWORD ? `Mínimo ${MIN_PASSWORD} caracteres.`
    : next && next === current ? 'Debe ser distinta de la actual.'
    : confirm && confirm !== next ? 'Las contraseñas no coinciden.'
    : null;
  const canSave = !!current && next.length >= MIN_PASSWORD && next === confirm && next !== current && !saving;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // Check the current password first (a stolen open session must not be enough)
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: authEmail, password: current });
      if (signInError) {
        setError('La contraseña actual no es correcta.');
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({ password: next });
      if (updateError) {
        setError(passwordError(updateError.message));
        return;
      }
      const { error: flagError } = await supabase.rpc('clear_must_change_password');
      if (flagError) throw flagError;
      setCurrent(''); setNext(''); setConfirm('');
      await qc.invalidateQueries({ queryKey: myAccountKey(user?.id) });
      toast({ title: 'Contraseña actualizada' });
      if (mustChange) navigate('/', { replace: true });
    } catch {
      setError('No se pudo cambiar la contraseña. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cambiar contraseña</CardTitle>
        <CardDescription>Usa al menos {MIN_PASSWORD} caracteres. No compartas tu contraseña.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          {/* Lets password managers attach the new password to the right account */}
          <input type="text" name="username" autoComplete="username" value={username} readOnly hidden />
          <div className="space-y-1.5">
            <Label htmlFor="pw-current">{mustChange ? 'Contraseña temporal' : 'Contraseña actual'}</Label>
            <Input id="pw-current" type={show ? 'text' : 'password'} autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pw-new">Contraseña nueva</Label>
              <Input id="pw-new" type={show ? 'text' : 'password'} autoComplete="new-password"
                value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw-confirm">Repite la contraseña nueva</Label>
              <Input id="pw-confirm" type={show ? 'text' : 'password'} autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
          </div>
          <button type="button" onClick={() => setShow((s) => !s)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            {show ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
            {show ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
          </button>
          {(problem || error) && (
            <p role="alert" className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 size-4 shrink-0" /> {error ?? problem}
            </p>
          )}
          <Button type="submit" disabled={!canSave}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Cambiar contraseña
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
